import { useQuery, useQueryClient } from '@tanstack/react-query';

import { UserStats } from '../types/networking';
import { apiMain } from 'services/apiService';

export function useUserStats(userId: string) {
  const queryClient = useQueryClient();
  const isDev = process.env.NODE_ENV === 'development';

  const { data, isLoading, error } = useQuery<UserStats, Error>({
    queryKey: ['userStats', userId],
    queryFn: async () => {
      if (isDev) console.log('[useUserStats] Fetching stats for userId:', userId);
      const res = await apiMain.get<UserStats>(`/networking/v1/stats/user/${userId}`);
      return res.data;
    },
    enabled: !!userId,
    staleTime: 1000 * 30, // 30 seconds - stats change more frequently
  });

  // Helper to manually update stats in cache (for optimistic updates)
  const setStats = (updater: UserStats | ((prev: UserStats | null) => UserStats | null)) => {
    queryClient.setQueryData<UserStats>(['userStats', userId], (old: UserStats | undefined) => {
      if (typeof updater === 'function') {
        return updater(old || null) as UserStats;
      }
      return updater;
    });
  };

  // Helper to control loading state (for compatibility)
  const setLoading = (loading: boolean) => {
    // With React Query, we don't manually control loading
    // This is kept for API compatibility but does nothing
    if (isDev) console.log('[useUserStats] setLoading called (no-op with React Query):', loading);
  };

  return {
    stats: data || null,
    loading: isLoading,
    error: error?.message || null,
    setLoading,
    setStats,
  };
}
